import { Users, BedDouble, ArrowUpRight } from "lucide-react";
import { useLang, BOOKING_URL } from "../i18n";
import { Reveal, Overline } from "./Reveal";
import { useLightbox } from "./Lightbox";

const roomMedia = {
  familiare: {
    main: "/assets/rooms/familiare/1.jpg",
    thumbs: [
      "/assets/rooms/familiare/2.jpg",
      "/assets/rooms/familiare/3.jpg",
      "/assets/rooms/familiare/bagno1.jpg",
      "/assets/rooms/familiare/bagno2.jpg",
      "/assets/rooms/familiare/bagno3.jpg",
    ],
  },
  matrimoniale: {
    main: "/assets/rooms/matrimoniale/2.jpg",
    thumbs: [
      "/assets/rooms/matrimoniale/3.jpg",
      "/assets/rooms/matrimoniale/4.jpg",
      "/assets/rooms/matrimoniale/bagno1.jpg",
      "/assets/rooms/matrimoniale/bagno2.jpg",
      "/assets/rooms/matrimoniale/1.jpg",
    ],
  },
  singola: {
    main: "/assets/rooms/singola/4.jpg",
    thumbs: [
      "/assets/rooms/singola/1.jpg",
      "/assets/rooms/singola/2.jpg",
      "/assets/rooms/singola/3.jpg",
      "/assets/rooms/singola/bagno1.jpg",
    ],
  },
};

const Rooms = () => {
  const { t } = useLang();
  const openLightbox = useLightbox();
  return (
    <section id="camere" data-testid="rooms-section" className="py-28 lg:py-40 bg-[#E9EDF6]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>{t.rooms.overline}</Overline>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl tracking-tight mt-4 max-w-xl">
              {t.rooms.title}
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#14213D]/60 leading-relaxed">{t.rooms.note}</p>
        </Reveal>

        <div className="mt-20 lg:mt-28 space-y-24 lg:space-y-36">
          {t.rooms.items.map((room, i) => {
            const media = roomMedia[room.key];
            return (
              <div key={room.key} className="grid lg:grid-cols-12 gap-10 items-center">
                <Reveal className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="overflow-hidden rounded-[1.5rem] group">
                    <img
                      src={media.main}
                      alt={room.name}
                      loading="lazy"
                      onClick={() => openLightbox(media.main, room.name)}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out cursor-zoom-in"
                    />
                  </div>
                  <div className="mt-4 flex gap-4 overflow-x-auto pb-2">
                    {media.thumbs.map((src, ti) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${room.name} — ${ti + 2}`}
                        loading="lazy"
                        onClick={() => openLightbox(src, room.name)}
                        className="h-24 w-32 object-cover rounded-xl flex-shrink-0 hover:scale-[1.04] transition-transform duration-500 cursor-zoom-in"
                      />
                    ))}
                  </div>
                </Reveal>
                <Reveal delay={0.15} className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <span className="font-serif text-6xl lg:text-7xl text-[#14213D]/15 leading-none">
                    0{i + 1}
                  </span>
                  <h3 className="font-serif text-3xl lg:text-4xl tracking-tight mt-4">{room.name}</h3>
                  <div className="mt-6 space-y-3 text-sm text-[#14213D]/70">
                    <p className="flex items-center gap-3">
                      <Users size={16} className="text-[#2852A6]" /> {room.guests}
                    </p>
                    <p className="flex items-center gap-3">
                      <BedDouble size={16} className="text-[#2852A6]" /> {room.bed}
                    </p>
                  </div>
                  <p className="mt-6 text-base text-[#14213D]/70 leading-relaxed max-w-md">{room.desc}</p>
                  <a
                    data-testid={`room-booking-cta-${room.key}`}
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-2 group text-sm tracking-wide"
                  >
                    <span className="link-underline">{t.rooms.cta}</span>
                    <ArrowUpRight size={16} className="text-[#D39A2E] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </a>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Rooms;
